import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export type CurrentListItem = {
  id:   string
  name: string
  qty:  number
}

export type CurrentShoppingList = {
  id:          string
  generatedAt: string
  itemCount:   number
  items:       CurrentListItem[]
} | null

export function useCurrentShoppingList(householdId: string | null): {
  list:    CurrentShoppingList
  loading: boolean
  error:   string | null
  refresh: () => void
} {
  const [list,    setList]    = useState<CurrentShoppingList>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [nonce,   setNonce]   = useState(0)

  useEffect(() => {
    if (!householdId) { setLoading(false); return }

    let cancelled = false
    setLoading(true)
    setError(null)

    ;(async () => {
      const { data, error } = await supabase
        .from('shopping_lists')
        .select(`
          id,
          generated_at,
          list_items(
            id,
            status,
            pantry_items(id, name)
          )
        `)
        .eq('household_id', householdId)
        .is('completed_at', null)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (cancelled) return

      if (error) { setError(error.message); setLoading(false); return }

      if (!data) { setList(null); setLoading(false); return }

      const activeItems: CurrentListItem[] = (data.list_items ?? [])
        .filter((row: any) => row.status !== 'removed')
        .map((row: any) => ({
          id:   (row.pantry_items as any)?.id   ?? row.id,
          name: (row.pantry_items as any)?.name ?? 'Unknown item',
          qty:  1,
        }))
        .filter((i: CurrentListItem) => i.name !== 'Unknown item')

      setList({
        id:          data.id,
        generatedAt: data.generated_at,
        itemCount:   activeItems.length,
        items:       activeItems,
      })
      setLoading(false)
    })()

    return () => { cancelled = true }
  }, [householdId, nonce])

  return { list, loading, error, refresh: () => setNonce(n => n + 1) }
}
